import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useClientAuth } from '../../context/ClientAuthContext';
import ClientNavbar from '../../components/ClientNavbar';
import clientAuthService from '../../services/clientAuthService';

const ClientProfile = () => {
  const navigate = useNavigate();
  const { portalClient, updatePortalClient } = useClientAuth();

  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (portalClient) setFullName(portalClient.full_name || '');
  }, [portalClient]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) { setError('Full name is required'); return; }
    if (fullName.trim().length < 3) { setError('Full name must be at least 3 characters'); return; }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await clientAuthService.updateProfile(fullName.trim());
      if (response.message) {
        setSuccess(response.message);
        if (response.user) updatePortalClient(response.user);
        setTimeout(() => navigate('/client/dashboard'), 2000);
      }
    } catch (err) {
      setError(err.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ClientNavbar />
      <Container className="mt-5" style={{ maxWidth: '600px' }}>
        <Card className="shadow">
          <Card.Header className="text-white" style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)' }}>
            <h4 className="mb-0">
              <i className="fas fa-user-edit me-2"></i>Edit Profile
            </h4>
          </Card.Header>
          <Card.Body>
            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                <i className="fas fa-check-circle me-2"></i>{success}
              </Alert>
            )}
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                <i className="fas fa-exclamation-circle me-2"></i>{error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="text-muted mb-3">Current Information</h6>
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Customer Code:</small>
                    <p className="mb-0 fw-bold">{portalClient?.customer_code}</p>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Email:</small>
                    <p className="mb-0 fw-bold">{portalClient?.email}</p>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Status:</small>
                    <p className="mb-0">
                      <span className={`badge ${portalClient?.email_verified ? 'bg-success' : 'bg-warning'}`}>
                        {portalClient?.email_verified ? 'Verified' : 'Not Verified'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <Form.Group className="mb-4">
                <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loading}
                  required
                  minLength={3}
                />
              </Form.Group>

              <div className="d-flex gap-2">
                <Button variant="primary" type="submit" disabled={loading} className="flex-grow-1">
                  {loading
                    ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Updating...</>
                    : <><i className="fas fa-save me-2"></i>Update Profile</>}
                </Button>
                <Button variant="secondary" onClick={() => navigate('/client/dashboard')} disabled={loading}>
                  <i className="fas fa-times me-2"></i>Cancel
                </Button>
              </div>
            </Form>

            <div className="mt-4 p-3 border border-info rounded bg-light">
              <small className="text-muted">
                <i className="fas fa-info-circle me-2 text-info"></i>
                <strong>Note:</strong> Customer Code and Email cannot be changed.
                If you need to update these fields, please contact the administrator.
              </small>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ClientProfile;
